import React, { useEffect } from 'react';
import { Form, Input, Card, Row, Col, Upload , Button } from 'antd';
import { PlusOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { notEmptyRule } from './validationUtils';
import ImageDisplayField from '../ImageDisplayField';


const { TextArea } = Input;

const VipClubSection = ({ 
  sectionKey, 
  sectionData, 
  uploadingImage, 
  uploadProps, 
  handleImageUploadForSection,
  handleImageUploadForItem,
  handleImageUploadForItemIcon,
  currentLanguage,
  form
}) => {
  const { t } = useTranslation();
  
  // Create dynamic field names based on current language
  const langKey = currentLanguage.toLowerCase();
  const getFieldName = (field) => ['content', langKey, sectionKey, field];

  // Define default structure for VIP Club items
  const defaultItem = {
    title: '',
    image: '',
    icon: '',
    button: '',
    description: '',
    advantages: []
  };

  useEffect(() => {
    const itemsFieldName = getFieldName('items');
    const currentItems = form.getFieldValue(itemsFieldName) || [];
    
    if (currentItems.length === 0) {
      form.setFieldsValue({
        [itemsFieldName.join('.')]: [defaultItem]
      });
    }
  }, []);

  return (
    <>
      {/* Regular section fields */}
      {sectionData.title !== undefined && (
        <Form.Item
          name={getFieldName('title')}
          label={t('Titolo')}
          rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
        >
          <Input placeholder={t('Inserisci il titolo')} />
        </Form.Item>
      )}

      {sectionData.description !== undefined && (
        <Form.Item
          name={getFieldName('description')}
          label={t('Descrizione')}
          rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
        >
          <TextArea rows={3} placeholder={t('Inserisci la descrizione')} />
        </Form.Item>
      )}

      {sectionData.button !== undefined && (
        <Form.Item
          name={getFieldName('button')}
          label={t('Testo del pulsante')}
          rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
        >
          <Input placeholder={t('Inserisci il testo del pulsante')} />
        </Form.Item>
      )}

      {/* Items section */}
      <Form.List name={getFieldName('items')}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              // Define default structure for VIP Club items
              const defaultItem = {
                title: '',
                image: '',
                icon: '',
                button: '',
                description: '',
                advantages: []
              };
              
                              const sampleItem = sectionData.items?.[0] || defaultItem;
                
                return (
                  <Card
                    key={key}
                    size='small'
                    style={{ marginBottom: 16 }}
                    extra={
                      fields.length > 1 && (
                        <Button
                          type='text'
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                          size='small'
                        >
                          {t('Rimuovi')}
                        </Button>
                      )
                    }
                  >
                    {sampleItem.title !== undefined && (
                      <Form.Item
                        {...restField}
                        name={[name, 'title']}
                        label={t('Titolo')}
                        rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                      >
                        <Input placeholder={t('Inserisci il titolo')} />
                      </Form.Item>
                    )}

                    {sampleItem.image !== undefined && (
                      <ImageDisplayField
                        name={[name, 'image']}
                        label={t('Immagine')}
                        uploadingImage={uploadingImage}
                        uploadProps={uploadProps}
                        onUpload={(file) => handleImageUploadForItem(file, sectionKey, name)}
                        onRemove={() => {
                          const currentValues = form.getFieldsValue();
                          const fieldPath = ['content', currentLanguage.toLowerCase(), sectionKey, 'items', name, 'image'];
                          let current = currentValues;
                          for (let i = 0; i < fieldPath.length - 1; i++) {
                            if (!current[fieldPath[i]]) {
                              current[fieldPath[i]] = {};
                            }
                            current = current[fieldPath[i]];
                          }
                          current[fieldPath[fieldPath.length - 1]] = '';
                          form.setFieldsValue(currentValues);
                        }}
                        rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                      />
                    )}

                    {sampleItem.icon !== undefined && (
                      <Row gutter={8}>
                        <Col flex='auto'>
                          <Form.Item
                            {...restField}
                            name={[name, 'icon']}
                            label={t('Icona')}
                            rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                          >
                            <Input placeholder={t('URL dell\'icona')} />
                          </Form.Item>
                        </Col>
                        <Col flex='none'>
                          <Form.Item label=' ' style={{ marginBottom: 0 }}>
                            <Upload
                              {...uploadProps}
                              beforeUpload={(file) => {
                                handleImageUploadForItemIcon(file, sectionKey, name);
                                return false;
                              }}
                              showUploadList={false}
                            >
                              <Button
                                icon={<UploadOutlined />}
                                loading={uploadingImage}
                                disabled={uploadingImage}
                              >
                                {t('Carica')}
                              </Button>
                            </Upload>
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {sampleItem.button !== undefined && (
                      <Form.Item
                        {...restField}
                        name={[name, 'button']}
                        label={t('Testo del pulsante')}
                        rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                      >
                        <Input placeholder={t('Inserisci il testo del pulsante')} />
                      </Form.Item>
                    )}

                    {sampleItem.description !== undefined && (
                      <Form.Item
                        {...restField}
                        name={[name, 'description']}
                        label={t('Descrizione')}
                      >
                        <TextArea rows={2} placeholder={t('Inserisci la descrizione')} />
                      </Form.Item>
                    )}

                    {sampleItem.advantages !== undefined && (
                      <Form.Item
                        {...restField}
                        name={[name, 'advantages']}
                        label={t('Vantaggi')}
                      >
                        <Form.List name={[name, 'advantages']}>
                          {(advantageFields, { add: addAdvantage, remove: removeAdvantage }) => (
                            <>
                              {advantageFields.map(({ key: advantageKey, name: advantageName, ...advantageRestField }) => (
                                <Row gutter={8} key={advantageKey} style={{ marginBottom: 8 }}>
                                  <Col flex='auto'>
                                    <Form.Item
                                      {...advantageRestField}
                                      name={[advantageName, 'title']}
                                      label={t('Titolo vantaggio')}
                                      rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                                    >
                                      <Input placeholder={t('Inserisci il titolo del vantaggio')} />
                                    </Form.Item>
                                  </Col>
                                  <Col flex='none'>
                                    {advantageFields.length > 1 && (
                                      <Button
                                        type='text'
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => removeAdvantage(advantageName)}
                                        size='small'
                                      >
                                        {t('Rimuovi')}
                                      </Button>
                                    )}
                                  </Col>
                                </Row>
                              ))}
                              <Button
                                type='dashed'
                                icon={<PlusOutlined />}
                                onClick={() => addAdvantage({ title: '' })}
                                size='small'
                              >
                                {t('Aggiungi vantaggio')}
                              </Button>
                            </>
                          )}
                        </Form.List>
                      </Form.Item>
                    )}
                  </Card>
                );
              })}
              <Button
                type='dashed'
                icon={<PlusOutlined />}
                onClick={() => add({ ...defaultItem })}
                style={{ width: '100%' }}
              >
                {t('Aggiungi elemento')}
              </Button>
            </>
          )}
        </Form.List>
    </>
  );
};

export default VipClubSection; 